import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Clock, Users, Sparkles, MapPin, Phone, Mail, ChevronDown,
  Crown, Camera, Music, Cake, Car, Flower2, Navigation
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { eventContent as defaultEventContent } from '../data/eventContent';
// Direct imports (not barrel) — the visual-edits babel plugin trips on
// top-level barrel `ImportDeclaration`s when tracing cross-file props.
import { GuestBook } from '../components/wedding/GuestFeatures';
import { Gallery } from '../components/wedding/Gallery';
import { OurStory } from '../components/wedding/OurStory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── small primitives ────────────────────────────────────────────── */

const SectionHeader = ({ eyebrow, title, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
    className="text-center mb-12 sm:mb-14"
  >
    <p className="font-cursive text-[#C8A951] text-xl sm:text-2xl mb-2">{eyebrow}</p>
    <h2
      data-testid={testid}
      className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3B2F2F] tracking-tight"
    >
      {title}
    </h2>
    <div className="gold-divider" />
  </motion.div>
);

const FadeIn = ({ delay = 0, children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ─── individual sections ─────────────────────────────────────────── */

const WelcomeSection = ({ c }) => (
  <section
    id="welcome"
    className="event-welcome"
    data-testid="event-welcome"
  >
    {/* Hero photo — served as picture<webp/jpg> for performance */}
    <picture className="event-welcome-bg" aria-hidden="true">
      <source srcSet="/images/event-hero.webp" type="image/webp" />
      <img src="/images/event-hero.jpg" alt="" className="event-welcome-bg-img" />
    </picture>
    <div className="event-welcome-overlay" aria-hidden="true" />
    <div className="relative z-10 max-w-3xl mx-auto px-6 py-24 sm:py-32 text-center text-white">
      <motion.p
        initial={{ opacity: 0, letterSpacing: '0.1em' }}
        animate={{ opacity: 1, letterSpacing: '0.32em' }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="text-[0.72rem] sm:text-xs uppercase tracking-[0.32em] text-[#E9D6A4] mb-6"
      >
        Welcome to Our Wedding Day
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: 0.2 }}
        className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight"
      >
        {c.shortNames.bride}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.45 }}
        className="font-cursive text-3xl sm:text-4xl my-2 text-[#E9D6A4]"
      >
        <span className="hero-ampersand-flame">&amp;</span>
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: 0.6 }}
        className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight"
      >
        {c.shortNames.groom}
      </motion.h1>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.95 }}
        className="my-8 flex items-center justify-center gap-3"
      >
        <span className="block w-8 h-px bg-[#C8A951]/70" />
        <p className="font-serif tracking-[0.32em] uppercase text-xs sm:text-sm">
          {c.date.full}
        </p>
        <span className="block w-8 h-px bg-[#C8A951]/70" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: 1.1 }}
        className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm sm:text-base mb-6 text-white/90"
      >
        <span className="inline-flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#E9D6A4]" />
          {c.venue.name}
        </span>
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(
            [c.venue.name, c.venue.address && c.venue.address !== 'TBD — full address' ? c.venue.address : '']
              .filter(Boolean)
              .join(', ')
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="event-maps-btn"
          data-testid="event-maps-btn"
          aria-label={`Open ${c.venue.name} in Maps`}
        >
          <Navigation className="w-3.5 h-3.5" />
          <span>Open in Maps</span>
        </a>
      </motion.div>

      {/* Static map preview — clickable, opens full Maps */}
      {c.venue.address && c.venue.address !== 'TBD — full address' && (
        <motion.a
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 1.2 }}
          href={`https://maps.google.com/?q=${encodeURIComponent(
            `${c.venue.name}, ${c.venue.address}`
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="event-map-preview"
          data-testid="event-map-preview"
          aria-label="Open White & Sable on Google Maps"
        >
          <iframe
            title="Venue map"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(
              c.venue.address
            )}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
            loading="lazy"
            allowFullScreen=""
            referrerPolicy="no-referrer-when-downgrade"
          />
          <span className="event-map-preview-overlay" aria-hidden="true">
            <span className="event-map-preview-pill">
              <Navigation className="w-3.5 h-3.5" />
              <span>{c.venue.address}</span>
            </span>
          </span>
        </motion.a>
      )}

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1.3 }}
        className="font-serif italic text-base sm:text-lg leading-relaxed text-white/85 max-w-xl mx-auto"
      >
        {c.welcomeMessage}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1.5 }}
        className="mt-10 font-cursive text-2xl sm:text-3xl text-[#E9D6A4]"
      >
        {c.motto}
      </motion.p>
    </div>
  </section>
);

const TimelineSection = ({ c }) => {
  // Render the timeline once, in the CMS-authored order, with defensive
  // de-duplication so a stray duplicate row (from an older CMS state or a
  // future accidental edit) can never surface twice on the public page.
  // Dedup key = time + title (case-insensitive, whitespace-normalized).
  // This keeps the couple's intended ordering intact — we do NOT re-sort
  // the entries — while guaranteeing the source-of-truth Admin/CMS data
  // is the single, canonical driver of what shows here.
  const seen = new Set();
  const items = (Array.isArray(c.timeline) ? c.timeline : []).filter((item) => {
    const key = `${(item.time || '').trim().toLowerCase()}|${(item.title || '').trim().toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
    <section id="day-timeline" className="section bg-cream" data-testid="event-timeline">
      <div className="max-w-3xl mx-auto px-4">
        <SectionHeader eyebrow="The Day at a Glance" title="Wedding Day Timeline" testid="event-timeline-title" />
        <div className="event-timeline" data-testid="event-timeline-list">
          {items.map((item, i) => (
            <FadeIn key={`${item.time}-${item.title}-${i}`} delay={i * 0.06} className="event-timeline-row" data-testid={`event-timeline-row-${i}`}>
              <div className="event-timeline-time">
                <Clock className="w-3.5 h-3.5 inline-block mr-2 -mt-0.5 text-[#C8A951]" />
                {item.time}
              </div>
              <div className="event-timeline-content">
                <h3 className="font-serif text-xl text-[#3B2F2F]">{item.title}</h3>
                <p className="text-sm text-[#5A4A4A] mt-1">{item.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

const PartyMember = ({ label, name }) => (
  <div className="event-party-card">
    <p className="event-party-label">{label}</p>
    <p className="event-party-name">{name}</p>
  </div>
);

const WeddingPartySection = ({ c }) => (
  <section id="party" className="section bg-ivory" data-testid="event-party">
    <div className="max-w-5xl mx-auto px-4">
      <SectionHeader eyebrow="Standing With Us" title="Wedding Party" testid="event-party-title" />

      <div className="event-party-grid">
        <PartyMember label="Officiant" name={c.party.officiant.name} />
        <PartyMember label="Best Man" name={c.party.bestMan.name} />
        <PartyMember label="Maid / Matron of Honor" name={c.party.matronOfHonor.name} />

        <div className="event-party-card">
          <p className="event-party-label">Bridesmaids</p>
          <ul className="event-party-list">
            {(c.party.bridesmaids || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
        <div className="event-party-card">
          <p className="event-party-label">Groomsmen</p>
          <ul className="event-party-list">
            {(c.party.groomsmen || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
        <div className="event-party-card">
          <p className="event-party-label">Flower Girls</p>
          <ul className="event-party-list">
            {(c.party.flowerGirls || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
        <div className="event-party-card">
          <p className="event-party-label">Ring Bearers</p>
          <ul className="event-party-list">
            {(c.party.ringBearers || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
        <div className="event-party-card">
          <p className="event-party-label">Parents of the Bride</p>
          <ul className="event-party-list">
            {(c.party.parentsOfBride || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
        <div className="event-party-card">
          <p className="event-party-label">Parents of the Groom</p>
          <ul className="event-party-list">
            {(c.party.parentsOfGroom || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const AttireCard = ({ piece }) => (
  <FadeIn className="event-attire-card">
    <Crown className="w-5 h-5 text-[#C8A951] mb-3" />
    <h3 className="font-serif text-xl text-[#3B2F2F] mb-3">{piece.label}</h3>
    <dl className="text-sm space-y-1.5">
      {Object.entries(piece).map(([k, v]) =>
        k === 'label' ? null : (
          <div key={k} className="event-attire-row">
            <dt className="text-[#9A8C7E] tracking-widest uppercase text-[0.65rem]">
              {k === 'madeIn' ? 'Made In' : k}
            </dt>
            <dd className="text-[#3B2F2F]">{v}</dd>
          </div>
        )
      )}
    </dl>
  </FadeIn>
);

const AttireSection = ({ c }) => (
  <section id="attire" className="section bg-cream" data-testid="event-attire">
    <div className="max-w-5xl mx-auto px-4">
      <SectionHeader eyebrow="What We Wore" title="Wedding Attire" testid="event-attire-title" />
      <div className="event-attire-grid">
        {Object.values(c.attire).map((piece) => (
          <AttireCard key={piece.label} piece={piece} />
        ))}
      </div>
    </div>
  </section>
);

const vendorIcon = (role) => {
  const r = role.toLowerCase();
  if (r.includes('photo')) return Camera;
  if (r.includes('video')) return Camera;
  if (r.includes('dj') || r.includes('music')) return Music;
  if (r.includes('cake')) return Cake;
  if (r.includes('transport')) return Car;
  if (r.includes('florist') || r.includes('decorator')) return Flower2;
  return Sparkles;
};

const VendorsSection = ({ c }) => (
  <section id="vendors" className="section bg-ivory" data-testid="event-vendors">
    <div className="max-w-5xl mx-auto px-4">
      <SectionHeader eyebrow="With Gratitude" title="Wedding Vendors" testid="event-vendors-title" />
      <div className="event-vendor-grid">
        {c.vendors.map((v, i) => {
          const Icon = vendorIcon(v.role);
          return (
            <FadeIn key={v.role} delay={(i % 3) * 0.05} className="event-vendor-card">
              <Icon className="w-5 h-5 text-[#C8A951] mb-3" />
              <p className="event-vendor-role">{v.role}</p>
              <p className="event-vendor-name">{v.name}</p>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);

const GuestInfoSection = ({ c }) => (
  <section id="guest-info" className="section bg-cream" data-testid="event-guest-info">
    <div className="max-w-4xl mx-auto px-4">
      <SectionHeader eyebrow="Things to Know" title="Guest Information" testid="event-guest-info-title" />
      <div className="event-info-grid">
        <FadeIn className="event-info-card">
          <Crown className="w-5 h-5 text-[#C8A951] mb-3" />
          <p className="event-info-label">Dress Code</p>
          <p className="event-info-title">{c.guestInfo.dressCode.title}</p>
          <p className="event-info-text">{c.guestInfo.dressCode.description}</p>
        </FadeIn>
        <FadeIn delay={0.05} className="event-info-card">
          <Car className="w-5 h-5 text-[#C8A951] mb-3" />
          <p className="event-info-label">Parking</p>
          <p className="event-info-text mt-2">{c.guestInfo.parking}</p>
        </FadeIn>
        <FadeIn delay={0.1} className="event-info-card">
          <Phone className="w-5 h-5 text-[#C8A951] mb-3" />
          <p className="event-info-label">{c.guestInfo.contact.name}</p>
          <p className="event-info-text mt-2 flex items-center gap-2">
            <Phone className="w-3.5 h-3.5 text-[#C8A951]" />{c.guestInfo.contact.phone}
          </p>
          <p className="event-info-text flex items-center gap-2 mt-1">
            <Mail className="w-3.5 h-3.5 text-[#C8A951]" />{c.guestInfo.contact.email}
          </p>
        </FadeIn>
      </div>
    </div>
  </section>
);

const FaqSection = ({ c }) => {
  const [open, setOpen] = useState(0);
  return (
    <section id="event-faq" className="section bg-ivory" data-testid="event-faq">
      <div className="max-w-3xl mx-auto px-4">
        <SectionHeader eyebrow="Common Questions" title="Frequently Asked" testid="event-faq-title" />
        <div className="event-faq">
          {c.faqs.map((f, i) => (
            <FadeIn key={f.q} delay={i * 0.04}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                className={`event-faq-item ${open === i ? 'is-open' : ''}`}
                data-testid={`event-faq-${i}`}
                aria-expanded={open === i}
              >
                <span className="event-faq-q">{f.q}</span>
                <ChevronDown className="event-faq-chev" />
              </button>
              <div className={`event-faq-a ${open === i ? 'is-open' : ''}`}>
                <p>{f.a}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

const EventFooter = ({ c }) => (
  <footer className="event-footer" data-testid="event-footer">
    <div className="max-w-3xl mx-auto px-6 py-14 text-center">
      <p className="font-cursive text-3xl sm:text-4xl text-[#C8A951]">
        {c.shortNames.bride} <span className="footer-ampersand-flame">&amp;</span> {c.shortNames.groom}
      </p>
      <p className="text-white/60 text-xs tracking-[0.32em] uppercase mt-3">{c.date.short}</p>
      <p className="text-white/70 italic mt-4 text-sm">{c.motto}</p>
      <div className="mt-6 flex items-center justify-center gap-4 text-xs text-white/40">
        <Link to="/" className="hover:text-[#E9D6A4] transition">Main Site</Link>
        <span>·</span>
        <Link to="/qr" className="hover:text-[#E9D6A4] transition">QR Code</Link>
      </div>
    </div>
  </footer>
);

/* ─── page ────────────────────────────────────────────────────────── */

const EventPage = () => {
  // Fetch live content from the CMS. Fall back to the static default so the
  // page keeps rendering during network hiccups. We also poll every 15s and
  // refetch when the tab regains focus so admin edits appear on the public
  // page without requiring a manual reload.
  const [content, setContent] = useState(defaultEventContent);

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        // Cache-buster keeps intermediate CDNs from serving stale responses
        const res = await axios.get(`${BACKEND_URL}/api/event/content`, {
          params: { _: Date.now() },
        });
        if (!alive || !res.data) return;
        setContent({ ...defaultEventContent, ...res.data });
      } catch { /* keep whatever's on screen */ }
    };

    fetchContent();
    const interval = setInterval(fetchContent, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchContent(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      alive = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div className="event-page" data-testid="event-page">
      <WelcomeSection c={content} />
      <TimelineSection c={content} />
      <WeddingPartySection c={content} />
      <AttireSection c={content} />
      <VendorsSection c={content} />
      <GuestInfoSection c={content} />
      <FaqSection c={content} />
      {content.showGallery && <Gallery />}
      {content.showLoveStory && <OurStory />}
      <GuestBook />
      <EventFooter c={content} />
    </div>
  );
};

export default EventPage;
